import React, { useEffect, useState, useRef, Component } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCapIcon, BriefcaseIcon, HeartIcon, AwardIcon, QuoteIcon, ChevronDownIcon, ArrowRightIcon, HeartHandshakeIcon, LightbulbIcon, StarIcon, SparklesIcon, CheckCircleIcon, LinkedinIcon, TwitterIcon, InstagramIcon, MailIcon, ChevronUpIcon, CalendarIcon, MapPinIcon } from 'lucide-react';
const FounderPage = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRefs = {
    hero: useRef(null),
    story: useRef(null),
    recognition: useRef(null),
    background: useRef(null),
    vision: useRef(null)
  };
  useEffect(() => {
    const handleScroll = () => {
      // Calculate scroll progress for progress indicator
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(Math.max(window.scrollY / totalHeight, 0), 1);
      setScrollProgress(progress);
      // Determine which section is currently in view
      const sectionEntries = Object.entries(sectionRefs);
      for (const [sectionId, ref] of sectionEntries) {
        if (!ref.current) continue;
        const rect = ref.current.getBoundingClientRect();
        if (rect.top <= window.innerHeight / 3 && rect.bottom >= window.innerHeight / 3) {
          setActiveSection(sectionId);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = sectionId => {
    sectionRefs[sectionId].current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="bg-white min-h-screen w-full" data-id="element-174">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 z-50 bg-emerald-100" data-id="element-175">
        <div className="h-full bg-emerald-500 transition-all duration-150 ease-out" style={{
        width: `${scrollProgress * 100}%`
      }} data-id="element-176"></div>
      </div>
      {/* Navigation Dots */}
      <div className="fixed right-8 top-1/2 transform -translate-y-1/2 z-50 hidden md:block" data-id="element-177">
        <div className="py-4 px-3 bg-white rounded-full shadow-md flex flex-col items-center space-y-6" data-id="element-178">
          {Object.entries(sectionRefs).map(([sectionId, _]) => <button key={sectionId} onClick={() => scrollToSection(sectionId)} className="group relative flex items-center justify-center" aria-label={`Scroll to ${sectionId} section`} data-id="element-179">
              <span className={`absolute -left-24 px-3 py-1 text-sm rounded-md text-gray-700 bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap`} data-id="element-180">
                {sectionId === 'hero' ? 'Introduction' : sectionId.charAt(0).toUpperCase() + sectionId.slice(1)}
              </span>
              <div className={`w-3 h-3 rounded-full transition-all duration-300 ${activeSection === sectionId ? 'bg-emerald-500 scale-125' : 'bg-gray-300 hover:bg-gray-400'}`} data-id="element-181" />
            </button>)}
        </div>
      </div>
      {/* Hero Section */}
      <section ref={sectionRefs.hero} className="relative min-h-screen flex items-center bg-white overflow-hidden" data-id="element-182">
        {/* Left Side - Background Gradient */}
        <div className="absolute top-0 left-0 w-full md:w-1/2 h-full bg-gradient-to-br from-emerald-900 to-teal-800 overflow-hidden" data-id="element-183">
          {/* Decorative Elements */}
          <div className="absolute inset-0 opacity-20" data-id="element-184">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_40%,rgba(16,185,129,0.3)_0%,transparent_60%)]" data-id="element-185"></div>
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_70%_60%,rgba(20,184,166,0.3)_0%,transparent_60%)]" data-id="element-186"></div>
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-emerald-500 opacity-20 blur-3xl" data-id="element-187"></div>
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-teal-500 opacity-20 blur-3xl" data-id="element-188"></div>
          </div>
          {/* Pattern Overlay */}
          <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIj48cGF0aCBkPSJNMzAgMEMxMy40MzEgMCAwIDEzLjQzMSAwIDMwYzAgMTYuNTY5IDEzLjQzMSAzMCAzMCAzMCAxNi41NjkgMCAzMC0xMy40MzEgMzAtMzBDNjAgMTMuNDMxIDQ2LjU2OSAwIDMwIDB6IiBmaWxsPSIjZmZmZmZmIiBmaWxsLW9wYWNpdHk9IjAuMDUiLz48L3N2Zz4=')]" data-id="element-189"></div>
          {/* Floating Elements */}
          <div className="absolute top-1/4 left-1/4 w-2 h-2 rounded-full bg-emerald-300/70 animate-float-slow" data-id="element-190"></div>
          <div className="absolute top-1/3 right-1/4 w-3 h-3 rounded-full bg-emerald-300/50 animate-float-medium" data-id="element-191"></div>
          <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 rounded-full bg-emerald-300/60 animate-float-fast" data-id="element-192"></div>
        </div>
        {/* Content Container */}
        <div className="container mx-auto px-6 py-12 flex flex-col md:flex-row items-center relative z-10" data-id="element-193">
          {/* Left Column - Text Content */}
          <div className="w-full md:w-1/2 text-center md:text-left mb-12 md:mb-0 text-white" data-id="element-194">
            <div className="max-w-xl mx-auto md:mx-0" style={{
            animation: 'fade-in-up 0.8s ease-out'
          }} data-id="element-195">
              <div className="inline-block mb-6 px-4 py-1 bg-white/10 backdrop-blur-sm text-white text-sm tracking-widest uppercase border border-white/20 rounded-sm" style={{
              animation: 'fade-in-up 0.8s ease-out'
            }} data-id="element-196">
                Visionary & Founder
              </div>
              <h1 className="text-5xl md:text-6xl font-light mb-6 tracking-tight leading-none" style={{
              animation: 'fade-in-up 1s ease-out 0.2s forwards',
              opacity: 0,
              transform: 'translateY(20px)'
            }} data-id="element-197">
                Diana <span className="text-emerald-300 font-medium" data-id="element-198">Ayoo</span>
              </h1>
              <p className="text-xl md:text-2xl text-white/90 font-light max-w-xl mb-10 leading-relaxed" style={{
              animation: 'fade-in-up 1.2s ease-out 0.4s forwards',
              opacity: 0,
              transform: 'translateY(20px)'
            }} data-id="element-199">
                The visionary behind Africa's first neurodivergent café,
                creating inclusive spaces where everyone belongs and thrives
              </p>
              <blockquote className="border-l-4 border-emerald-400/40 pl-4 mb-10 italic text-white/90" style={{
              animation: 'fade-in-up 1.3s ease-out 0.5s forwards',
              opacity: 0,
              transform: 'translateY(20px)'
            }} data-id="element-200">
                "I envision a continent where neurodivergent individuals can
                thrive, not just survive — where they are valued for their
                unique perspectives and talents."
              </blockquote>
              <div className="flex flex-wrap gap-4 mb-10" style={{
              animation: 'fade-in-up 1.3s ease-out 0.5s forwards',
              opacity: 0,
              transform: 'translateY(20px)'
            }} data-id="element-201">
                <a href="#" className="text-white/80 hover:text-white transition-all duration-300 hover:scale-110" data-id="element-202">
                  <LinkedinIcon size={22} data-id="element-203" />
                </a>
                <a href="#" className="text-white/80 hover:text-white transition-all duration-300 hover:scale-110" data-id="element-204">
                  <TwitterIcon size={22} data-id="element-205" />
                </a>
                <a href="#" className="text-white/80 hover:text-white transition-all duration-300 hover:scale-110" data-id="element-206">
                  <InstagramIcon size={22} data-id="element-207" />
                </a>
                <a href="#" className="text-white/80 hover:text-white transition-all duration-300 hover:scale-110" data-id="element-208">
                  <MailIcon size={22} data-id="element-209" />
                </a>
              </div>
              <button onClick={() => scrollToSection('story')} className="text-white border border-emerald-400/30 bg-emerald-500/10 hover:bg-emerald-500/20 px-6 py-3 rounded-sm transition-all flex items-center group" aria-label="Scroll to Diana's story" style={{
              animation: 'fade-in-up 1.4s ease-out 0.6s forwards',
              opacity: 0,
              transform: 'translateY(20px)'
            }} data-id="element-210">
                <span className="mr-3 group-hover:translate-y-0.5 transition-transform duration-300" data-id="element-211">
                  Discover her story
                </span>
                <ChevronDownIcon size={20} className="animate-bounce" data-id="element-212" />
              </button>
            </div>
          </div>
          {/* Right Column - Diana's Image */}
          <div className="w-full md:w-1/2 flex items-center justify-center" data-id="element-213">
            <div className="relative" style={{
            animation: 'fade-in 1.5s ease-out 0.3s forwards',
            opacity: 0
          }} data-id="element-214">
              {/* Image Container with elegant frame effect */}
              <div className="relative overflow-hidden rounded-sm shadow-2xl border-8 border-white/10" data-id="element-215">
                <img src="https://joyfultherapy.health/wp-content/uploads/2024/10/Diana-Ayoo-Ayiras-founder-1.jpeg" alt="Diana Ayoo, Founder of Ayira's Cafe" className="w-full h-auto max-w-md object-cover" data-id="element-216" />
                {/* Subtle gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/40 to-transparent" data-id="element-217"></div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-emerald-300/50" data-id="element-218"></div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-emerald-300/50" data-id="element-219"></div>
              {/* Credentials badges */}
              <div className="absolute -right-6 top-1/4 bg-white text-emerald-800 text-sm font-medium px-4 py-2 rounded-sm shadow-lg transform rotate-3" data-id="element-220">
                Founder & Visionary
              </div>
              <div className="absolute -left-6 bottom-1/4 bg-white text-amber-800 text-sm font-medium px-4 py-2 rounded-sm shadow-lg transform -rotate-3" data-id="element-221">
                Inclusion Champion
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Introduction - A Mother's Mission */}
      <section ref={sectionRefs.story} className="py-24 px-6 bg-white" data-id="element-222">
        <div className="container mx-auto max-w-4xl" data-id="element-223">
          <div className="text-center mb-16" data-id="element-224">
            <div className="inline-block w-24 h-0.5 bg-emerald-200 mb-8" data-id="element-225"></div>
            <h2 className="text-4xl font-light text-gray-900 mb-12 tracking-tight" data-id="element-226">
              A Mother's Mission
            </h2>
            <div className="relative" data-id="element-227">
              <div className="absolute -left-16 -top-8 text-9xl text-emerald-50 font-serif" data-id="element-228">
                "
              </div>
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-8 relative z-10" data-id="element-229">
                My name is Diana Ayoo, and I'm the mother of a neurodivergent
                child, Ayira — the inspiration behind everything we do. When
                Ayira was diagnosed, I was overwhelmed. In Kenya, I couldn't
                find a single space that truly welcomed her.
              </p>
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-12 relative z-10" data-id="element-230">
                In 2018, I took Ayira to Boston for diagnosis and support. That
                experience changed our lives. I came back not only with answers,
                but with a determination to build what I couldn't find — here at
                home.
              </p>
              <div className="absolute -right-16 -bottom-8 text-9xl text-emerald-50 font-serif rotate-180" data-id="element-231">
                "
              </div>
            </div>
          </div>
          {/* Journey Timeline */}
          <div className="mt-20 mb-16 relative" data-id="element-232">
            <h3 className="text-2xl font-light text-center mb-12" data-id="element-233">
              The Journey of Ayira's
            </h3>
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-emerald-100 transform -translate-x-1/2" data-id="element-234"></div>
            <TimelineEvent year="2018" title="The Vision Takes Root" description="After traveling to Boston for Ayira's diagnosis, Diana returns to Kenya with a clear vision for creating inclusive spaces." side="left" icon={<MapPinIcon size={20} data-id="element-236" />} iconBg="bg-purple-100" iconColor="text-purple-600" data-id="element-235" />
            <TimelineEvent year="2020" title="Foundation Established" description="Diana founds Ayira's organization with a mission to support neurodivergent individuals and their families across Kenya." side="right" icon={<CalendarIcon size={20} data-id="element-238" />} iconBg="bg-emerald-100" iconColor="text-emerald-600" data-id="element-237" />
            <TimelineEvent year="2022" title="Café Opening" description="Ayira's Neuro-Soul Café opens its doors, becoming Africa's first neurodivergent café and creating employment opportunities." side="left" icon={<HeartHandshakeIcon size={20} data-id="element-240" />} iconBg="bg-amber-100" iconColor="text-amber-600" data-id="element-239" />
            <TimelineEvent year="2024" title="National Recognition" description="Diana receives the Top National Inclusion Champion award at the DIAR Awards, highlighting her pioneering work." side="right" icon={<AwardIcon size={20} data-id="element-242" />} iconBg="bg-blue-100" iconColor="text-blue-600" data-id="element-241" />
          </div>
          <div className="flex justify-center" data-id="element-243">
            <Link to="/programs" className="group inline-flex items-center text-lg border-b-2 border-emerald-300 text-gray-800 font-light py-1 transition-all hover:border-emerald-500" data-id="element-244">
              Discover our programs
              <ArrowRightIcon size={20} className="ml-2 group-hover:translate-x-2 transition-transform duration-300" data-id="element-245" />
            </Link>
          </div>
        </div>
      </section>
      {/* Quote Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-emerald-50 to-teal-50 overflow-hidden relative" data-id="element-246">
        <div className="absolute inset-0 opacity-5" data-id="element-247">
          <div className="absolute -left-20 top-10" data-id="element-248">
            <QuoteIcon size={240} data-id="element-249" />
          </div>
          <div className="absolute -right-20 bottom-10 rotate-180" data-id="element-250">
            <QuoteIcon size={240} data-id="element-251" />
          </div>
        </div>
        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-252">
          <div className="text-center" data-id="element-253">
            <QuoteIcon size={48} className="text-emerald-400 mx-auto mb-8" data-id="element-254" />
            <blockquote className="text-2xl md:text-3xl font-light text-gray-800 italic mb-10 leading-relaxed" data-id="element-255">
              "That vision became Ayira's: a first-of-its-kind social enterprise
              that integrates inclusive employment, remote ABA therapy, and
              caregiver empowerment under one roof."
            </blockquote>
            <div className="inline-block w-20 h-0.5 bg-emerald-300 mb-6" data-id="element-256"></div>
            <p className="text-gray-500 font-medium tracking-wide" data-id="element-257">
              Diana Ayoo
            </p>
          </div>
        </div>
      </section>
      {/* Recognition & Impact */}
      <section ref={sectionRefs.recognition} className="py-24 px-6 bg-white" data-id="element-258">
        <div className="container mx-auto max-w-5xl" data-id="element-259">
          <div className="text-center mb-20" data-id="element-260">
            <div className="inline-block w-24 h-0.5 bg-amber-200 mb-8" data-id="element-261"></div>
            <h2 className="text-4xl font-light text-gray-900 mb-6 tracking-tight" data-id="element-262">
              Recognition & Impact
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto" data-id="element-263">
              Diana's vision and leadership have created meaningful change
            </p>
          </div>
          {/* Featured Recognition */}
          <div className="mb-20 bg-gradient-to-r from-amber-50 to-amber-100/50 p-10 rounded-sm shadow-sm relative overflow-hidden" data-id="element-264">
            <div className="absolute top-0 right-0 w-40 h-40 -translate-y-1/2 translate-x-1/2" data-id="element-265">
              <div className="w-full h-full rounded-full bg-amber-200 opacity-20" data-id="element-266"></div>
            </div>
            <div className="flex flex-col md:flex-row items-center" data-id="element-267">
              <div className="md:w-1/3 mb-8 md:mb-0 relative" data-id="element-268">
                <div className="w-32 h-32 mx-auto rounded-full bg-white shadow-md flex items-center justify-center relative z-10" data-id="element-269">
                  <AwardIcon size={48} className="text-amber-500" data-id="element-270" />
                </div>
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-32 rounded-full border-4 border-dashed border-amber-200 animate-slow-spin" data-id="element-271"></div>
              </div>
              <div className="md:w-2/3 md:pl-10" data-id="element-272">
                <h3 className="text-2xl font-medium mb-4 text-gray-800" data-id="element-273">
                  DIAR Awards – Top National Inclusion Champion 2024
                </h3>
                <p className="text-lg text-gray-600 font-light mb-6 leading-relaxed" data-id="element-274">
                  Diana was honored as the Top National Inclusion Champion,
                  highlighting her groundbreaking leadership in advancing
                  neurodiversity inclusion in Kenya and setting a precedent for
                  the entire continent.
                </p>
                <div className="bg-white/50 p-4 rounded-sm" data-id="element-275">
                  <p className="text-center text-sm font-medium text-amber-700" data-id="element-276">
                    "For exceptional vision and commitment to creating inclusive
                    spaces for neurodivergent individuals"
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-277">
            <ImpactCard icon={<AwardIcon size={32} data-id="element-279" />} title="National Recognition" description="Pioneering neurodiversity inclusion across Africa" color="amber" data-id="element-278" />
            <ImpactCard icon={<HeartIcon size={32} data-id="element-281" />} title="Lives Impacted" description="Creating opportunities for neurodivergent individuals" color="emerald" data-id="element-280" />
            <ImpactCard icon={<BriefcaseIcon size={32} data-id="element-283" />} title="Employment Created" description="Jobs for neurodivergent individuals" color="amber" data-id="element-282" />
            <ImpactCard icon={<GraduationCapIcon size={32} data-id="element-285" />} title="Caregivers Trained" description="Through the Joyful Journeys program" color="emerald" data-id="element-284" />
          </div>
        </div>
      </section>
      {/* Professional Background */}
      <section ref={sectionRefs.background} className="py-24 px-6 bg-gradient-to-b from-white to-gray-50" data-id="element-286">
        <div className="container mx-auto max-w-5xl" data-id="element-287">
          <div className="text-center mb-20" data-id="element-288">
            <div className="inline-block w-24 h-0.5 bg-purple-200 mb-8" data-id="element-289"></div>
            <h2 className="text-4xl font-light text-gray-900 mb-6 tracking-tight" data-id="element-290">
              Professional Background
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto" data-id="element-291">
              A unique blend of technical expertise and personal insight
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-292">
            <BackgroundCard icon={<GraduationCapIcon size={32} data-id="element-294" />} title="Education & Certification" color="purple" items={['Certified Behaviour Analyst (QBA, IBAO)', 'NITA-accredited trainer', 'Specialized training in Applied Behavior Analysis']} data-id="element-293" />
            <BackgroundCard icon={<BriefcaseIcon size={32} data-id="element-296" />} title="Career Experience" color="teal" items={['Engineer in sales and marketing', 'Led inclusive digital innovation projects', "Experience across Africa's tech and telecom sector"]} data-id="element-295" />
          </div>
          <div className="mt-20 p-12 bg-white shadow-sm border border-gray-100 rounded-sm relative overflow-hidden" data-id="element-297">
            <div className="absolute -right-10 -bottom-10 text-gray-50 opacity-20" data-id="element-298">
              <QuoteIcon size={200} data-id="element-299" />
            </div>
            <p className="italic text-gray-600 text-center text-xl relative z-10 max-w-3xl mx-auto" data-id="element-300">
              "I bring both worlds together—technical expertise and deep
              personal insight—to design solutions that are scalable,
              affordable, and truly human-centered."
            </p>
          </div>
        </div>
      </section>
      {/* Vision */}
      <section ref={sectionRefs.vision} className="py-24 px-6 bg-white relative overflow-hidden" data-id="element-301">
        <div className="absolute inset-0 opacity-5" data-id="element-302">
          <div className="absolute -left-40 top-20" data-id="element-303">
            <LightbulbIcon size={300} data-id="element-304" />
          </div>
          <div className="absolute -right-40 bottom-20" data-id="element-305">
            <HeartHandshakeIcon size={300} data-id="element-306" />
          </div>
        </div>
        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-307">
          <div className="text-center mb-20" data-id="element-308">
            <div className="inline-block w-24 h-0.5 bg-emerald-200 mb-8" data-id="element-309"></div>
            <h2 className="text-4xl font-light text-gray-900 mb-12 tracking-tight" data-id="element-310">
              Diana's Vision for Africa
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-8" data-id="element-311">
              Ayira's is a café, yes — but it's also a therapy space, a training
              ground, a safe haven. Every cup of coffee, every job created,
              every parent empowered is a step toward a more inclusive Kenya.
            </p>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-16" data-id="element-312">
              I envision a continent where neurodivergent individuals can
              thrive, not just survive — where they are valued for their unique
              perspectives and talents, and where systems are designed with
              their needs in mind.
            </p>
          </div>
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-12 rounded-sm shadow-sm border border-emerald-100" data-id="element-313">
            <div className="text-emerald-500 mb-10 w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto shadow-md" data-id="element-314">
              <HeartHandshakeIcon size={36} data-id="element-315" />
            </div>
            <h3 className="text-2xl font-medium mb-8 text-gray-800 text-center" data-id="element-316">
              Join Our Movement
            </h3>
            <p className="text-gray-600 mb-12 text-center text-lg max-w-xl mx-auto" data-id="element-317">
              Be part of our mission to create a more inclusive Africa where
              neurodivergent individuals can thrive and belong.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center" data-id="element-318">
              <Link to="/programs" className="border-2 border-emerald-500 bg-white text-emerald-700 px-10 py-4 hover:bg-emerald-500 hover:text-white transition-all duration-300 font-medium rounded-sm shadow-sm hover:shadow-md flex items-center justify-center" data-id="element-319">
                <span data-id="element-320">Explore Our Programs</span>
                <ArrowRightIcon size={18} className="ml-2" data-id="element-321" />
              </Link>
              <Link to="/future-hub" className="border-2 border-emerald-500 bg-emerald-500 text-white px-10 py-4 hover:bg-emerald-600 transition-all duration-300 font-medium rounded-sm shadow-sm hover:shadow-md" data-id="element-322">
                Learn About Our Future Hub
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Media Recognition */}
      <section className="py-24 px-6 bg-gradient-to-r from-amber-50 to-amber-100/50" data-id="element-323">
        <div className="container mx-auto max-w-5xl" data-id="element-324">
          <div className="text-center mb-16" data-id="element-325">
            <div className="inline-block w-24 h-0.5 bg-amber-200 mb-8" data-id="element-326"></div>
            <h2 className="text-4xl font-light text-gray-900 mb-6 tracking-tight" data-id="element-327">
              Media Coverage
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto" data-id="element-328">
              Diana's work has been recognized by major media outlets
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-329">
            <MediaCard outlet="BBC News Africa" quote="Diana Ayoo created a café inspired by her neurodiverse daughter Ayira..." data-id="element-330" />
            <MediaCard outlet="Voice of America" quote="Diana Ayoo opened Ayira's Neuro-Soul Café as a haven for people with neurological disorders..." data-id="element-331" />
            <MediaCard outlet="Nation.co.ke" quote="Ayira's Café has become renowned for hiring members of staff diagnosed with neuro-diverse challenges." data-id="element-332" />
          </div>
          <div className="text-center mt-12" data-id="element-333">
            <Link to="/media" className="group inline-flex items-center text-lg border-b-2 border-amber-300 text-gray-800 font-light py-1 transition-all hover:border-amber-500" data-id="element-334">
              View all media coverage
              <ArrowRightIcon size={20} className="ml-2 group-hover:translate-x-2 transition-transform duration-300" data-id="element-335" />
            </Link>
          </div>
        </div>
      </section>
      {/* Contact Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-emerald-900 to-teal-900 text-white" data-id="element-336">
        <div className="container mx-auto max-w-4xl" data-id="element-337">
          <div className="text-center mb-16" data-id="element-338">
            <div className="inline-block w-24 h-0.5 bg-emerald-300/30 mb-8" data-id="element-339"></div>
            <h2 className="text-4xl font-light text-white mb-6 tracking-tight" data-id="element-340">
              Connect with Diana
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto" data-id="element-341">
              For speaking engagements and collaboration opportunities
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm p-12 rounded-sm border border-white/20 shadow-lg" data-id="element-342">
            <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-343">
              <div className="md:w-1/2 text-center md:text-left" data-id="element-344">
                <h3 className="text-2xl font-medium mb-6" data-id="element-345">Diana Ayoo</h3>
                <p className="text-white/80 mb-8" data-id="element-346">
                  Founder of Ayira's Neuro-Soul Café, speaker, and advocate for
                  neurodiversity inclusion across Africa.
                </p>
                <div className="flex justify-center md:justify-start space-x-4" data-id="element-347">
                  <SocialButton icon={<LinkedinIcon size={20} data-id="element-349" />} data-id="element-348" />
                  <SocialButton icon={<TwitterIcon size={20} data-id="element-351" />} data-id="element-350" />
                  <SocialButton icon={<InstagramIcon size={20} data-id="element-353" />} data-id="element-352" />
                  <SocialButton icon={<MailIcon size={20} data-id="element-355" />} data-id="element-354" />
                </div>
              </div>
              <div className="md:w-1/2" data-id="element-356">
                <div className="bg-white/50 p-6 rounded-sm border border-white/10" data-id="element-357">
                  <h4 className="text-xl font-medium mb-4" data-id="element-358">
                    Speaking Inquiries
                  </h4>
                  <p className="text-white/80 mb-6" data-id="element-359">
                    Diana is available for speaking engagements on
                    neurodiversity inclusion, social entrepreneurship, and
                    inclusive design.
                  </p>
                  <a href="mailto:contact@ayirascafe.com" className="block w-full bg-emerald-500 hover:bg-emerald-600 text-white text-center py-3 rounded-sm transition-colors" data-id="element-360">
                    Contact for Bookings
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Back to Top Button */}
      <div className="fixed bottom-8 right-8 z-50" data-id="element-361">
        <button onClick={() => window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })} className="bg-emerald-500 text-white w-12 h-12 rounded-full flex items-center justify-center shadow-md hover:bg-emerald-600 transition-all duration-300 hover:shadow-lg" aria-label="Scroll to top" data-id="element-362">
          <ChevronUpIcon size={24} data-id="element-363" />
        </button>
      </div>
      {/* Add keyframes for animations */}
      <style jsx data-id="element-364">{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes float-slow {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-12px);
          }
        }
        @keyframes float-medium {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        @keyframes float-fast {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        @keyframes slow-spin {
          from {
            transform: translate(-50%, 0) rotate(0deg);
          }
          to {
            transform: translate(-50%, 0) rotate(360deg);
          }
        }
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
        .animate-float-medium {
          animation: float-medium 4.5s ease-in-out infinite;
        }
        .animate-float-fast {
          animation: float-fast 3.5s ease-in-out infinite;
        }
        .animate-slow-spin {
          animation: slow-spin 20s linear infinite;
        }
      `}</style>
    </div>;
};
// Component for timeline events
const TimelineEvent = ({
  year,
  title,
  description,
  side,
  icon,
  iconBg,
  iconColor
}) => {
  return <div className={`flex items-center mb-16 ${side === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-365">
      <div className={`w-1/2 ${side === 'right' ? 'pl-8 text-left' : 'pr-8 text-right'}`} data-id="element-366">
        <div className={`inline-block mb-2 px-3 py-1 bg-gray-100 rounded-full text-sm font-medium ${side === 'right' ? '' : 'ml-auto'}`} data-id="element-367">
          {year}
        </div>
        <h3 className="text-xl font-medium mb-2 text-gray-800" data-id="element-368">{title}</h3>
        <p className="text-gray-600 font-light" data-id="element-369">{description}</p>
      </div>
      <div className="relative flex-shrink-0" data-id="element-370">
        <div className={`w-12 h-12 rounded-full ${iconBg} ${iconColor} flex items-center justify-center z-10 relative shadow-md`} data-id="element-371">
          {icon}
        </div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border-2 border-dashed border-emerald-200 animate-slow-spin opacity-70" data-id="element-372"></div>
      </div>
      <div className={`w-1/2 ${side === 'right' ? 'pr-8' : 'pl-8'}`} data-id="element-373"></div>
    </div>;
};
// Component for impact cards
const ImpactCard = ({
  icon,
  title,
  description,
  color
}) => {
  const colorClasses = {
    amber: {
      icon: 'text-amber-500 bg-amber-50',
      hover: 'group-hover:shadow-amber-100'
    },
    emerald: {
      icon: 'text-emerald-500 bg-emerald-50',
      hover: 'group-hover:shadow-emerald-100'
    }
  };
  return <div className="group" data-id="element-374">
      <div className={`p-8 bg-white border border-gray-100 shadow-sm rounded-sm transition-all duration-500 group-hover:shadow-lg ${colorClasses[color].hover} group-hover:-translate-y-1 h-full`} data-id="element-375">
        <div className={`w-16 h-16 flex items-center justify-center mx-auto mb-6 ${colorClasses[color].icon} rounded-full transition-all duration-500 group-hover:scale-110`} data-id="element-376">
          {icon}
        </div>
        <h4 className="text-xl font-medium mb-4 text-gray-800 text-center" data-id="element-377">
          {title}
        </h4>
        <p className="text-gray-600 font-light text-center" data-id="element-378">{description}</p>
      </div>
    </div>;
};
// Component for background cards
const BackgroundCard = ({
  icon,
  title,
  color,
  items
}) => {
  const colorClasses = {
    purple: {
      icon: 'text-purple-500 bg-purple-50',
      star: 'text-purple-400',
      hover: 'group-hover:text-purple-700'
    },
    teal: {
      icon: 'text-teal-500 bg-teal-50',
      star: 'text-teal-400',
      hover: 'group-hover:text-teal-700'
    }
  };
  return <div className="bg-white p-10 shadow-sm border border-gray-100 rounded-sm group hover:shadow-lg transition-all duration-500 hover:-translate-y-1" data-id="element-379">
      <div className={`${colorClasses[color].icon} mb-8 w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110`} data-id="element-380">
        {icon}
      </div>
      <h3 className={`text-2xl font-medium mb-8 text-gray-800 transition-colors ${colorClasses[color].hover}`} data-id="element-381">
        {title}
      </h3>
      <ul className="space-y-5" data-id="element-382">
        {items.map((item, index) => <li key={index} className="flex items-start" data-id="element-383">
            <div className={`mr-4 mt-1 ${colorClasses[color].star}`} data-id="element-384">
              <CheckCircleIcon size={18} data-id="element-385" />
            </div>
            <span className="text-gray-600 text-lg" data-id="element-386">{item}</span>
          </li>)}
      </ul>
    </div>;
};
// Component for media cards
const MediaCard = ({
  outlet,
  quote
}) => {
  return <div className="bg-white p-8 rounded-sm shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 hover:-translate-y-1" data-id="element-387">
      <div className="flex items-center mb-6" data-id="element-388">
        <QuoteIcon size={24} className="text-amber-400 mr-3" data-id="element-389" />
        <h3 className="font-medium text-gray-800" data-id="element-390">{outlet}</h3>
      </div>
      <blockquote className="text-gray-600 italic" data-id="element-391">"{quote}"</blockquote>
    </div>;
};
// Component for social buttons
const SocialButton = ({
  icon
}) => {
  return <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-id="element-392">
      {icon}
    </a>;
};
export default FounderPage;